import React from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';

export interface Package {
  id: string;
  name: string;
  description: string;
  duration_months: number;
  components: Array<{
    id: string;
    component_type: string;
    name: string;
    quantity: number;
    unit_price_dollars: number;
    sort_order: number;
    session_length_minutes?: number | null;
    session_link?: string | null;
  }>;
  totalCost: number;
  is_active: boolean;
  created_at: string;
}

const LINK_ENABLED_GUIDE_ID = '47e9e849-d537-4668-afa6-8ff6123dc520';

interface PackageListProps {
  packages: Package[];
  onEdit?: (pkg: Package) => void;
  onDelete?: (id: string) => void;
  onCreate?: () => void;
  onSelect?: (pkg: Package) => void;
  readOnly?: boolean;
  loading?: boolean;
  guideId?: string | null;
}

const PackageList: React.FC<PackageListProps> = ({
  packages,
  onEdit,
  onDelete,
  onCreate,
  onSelect,
  readOnly = false,
  loading = false,
  guideId
}) => {
  const showLinks = guideId === LINK_ENABLED_GUIDE_ID;
  const getComponentIcon = (type: string) => {
    switch (type) {
      case 'preparation': return '/clipboard.svg';
      case 'ceremony': return '/star.svg';
      case 'integration': return '/heart.svg';
      case 'additional': return '/plus.svg';
      default: return '•';
    }
  };

  const formatComponentType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (loading) {
    return (
      <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg overflow-hidden text-[#2C2731]">
        <div className="px-6 py-4 border-b border-[#2C2731] bg-[#2C2731]">
          <h2 className="text-base font-medium text-[#FEF6D6] font-recoleta">Saved Packages</h2>
        </div>
        <div className="p-8 text-center text-[#2C2731]/70">
          <div>Loading packages...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FFFCEF] border border-[#2C2731] rounded-lg overflow-hidden text-[#2C2731]">
      <div className="px-6 py-4 border-b border-[#2C2731] bg-[#2C2731]">
        <h2 className="text-base font-medium text-[#FEF6D6] font-recoleta">Saved Packages</h2>
      </div>
      
      {packages.length === 0 ? (
        <div className="p-8 text-center text-[#2C2731]/80">
          <div className="mb-4">
            <div className="w-12 h-12 mx-auto bg-[#E8DFBD] border border-[#2C2731] rounded-lg flex items-center justify-center text-[#2C2731]">
              <PlusIcon className="w-6 h-6" />
            </div>
          </div>
          <h3 className="text-sm font-medium text-[#2C2731] mb-1">No packages yet</h3>
          <p className="text-sm mb-4">{readOnly ? 'Packages you create will appear here.' : 'Get started by creating your first package'}</p>
          {!readOnly && onCreate && (
            <button
              onClick={onCreate}
              className="inline-flex items-center px-3 py-2 bg-[#2C2731] border border-[#2C2731] text-[#FEF6D6] text-sm font-medium rounded-md hover:bg-[#241F2A] transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Package
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-0">
          {packages.map((pkg, index) => (
            <div
              key={pkg.id}
              className={`p-6 ${index < packages.length - 1 ? 'border-b border-[#2C2731]/40' : ''} ${readOnly && onSelect ? 'cursor-pointer hover:bg-[#F3EBCD] transition-colors' : ''}`}
              onClick={() => {
                if (readOnly && onSelect) {
                  onSelect(pkg);
                }
              }}
              role={readOnly && onSelect ? 'button' : undefined}
              tabIndex={readOnly && onSelect ? 0 : undefined}
              onKeyDown={(event) => {
                if (!readOnly || !onSelect) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(pkg);
                }
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <div>
                      <h3 className="font-medium text-[#2C2731]">{pkg.name}</h3>
                      <p className="text-sm text-[#2C2731]/80">{pkg.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-semibold text-[#2C2731]">${pkg.totalCost.toLocaleString()}</div>
                      <div className="text-xs text-[#2C2731]/70">{pkg.duration_months} month{pkg.duration_months !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  
                  {/* Components Display */}
                  <div className="flex flex-wrap gap-3">
                    {pkg.components.map((component) => (
                      <div key={component.id} className="inline-flex items-center gap-1 px-2 py-1 bg-[#E8DFBD] border border-[#2C2731] rounded text-xs text-[#2C2731]">
                        <span><img src={getComponentIcon(component.component_type)} alt={formatComponentType(component.component_type)} className="w-3 h-3" /></span>
                        <span className="font-medium">{formatComponentType(component.component_type)}:</span>
                        <span>${component.unit_price_dollars} × {component.quantity}</span>
                        {showLinks && component.session_link && (
                          <span className="ml-1 text-[#2C2731]/60" title={component.session_link}>🔗</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {!readOnly && (
                  <div className="flex items-center gap-2 ml-4">
                    {onEdit && (
                      <button
                        onClick={() => onEdit(pkg)}
                        className="p-2 text-[#2C2731]/60 hover:text-[#2C2731] transition-colors"
                        title="Edit package"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(pkg.id)}
                        className="p-2 text-[#2C2731]/60 hover:text-[#2C2731] transition-colors"
                        title="Delete package"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PackageList;
